import { TFunction } from "i18next";
import { NamedEntity } from "../../shared/CaseModel";

export type Breadcrumb = {
  label: string;
  // the page the breadcrumb leads to
  to: string;
};

/** What the pages directly under the root are called. */
const ROOT_LABEL_KEYS: { [segment: string]: string } = {
  cases: "home.section.cases.title",
  portfolio: "home.section.portfolio.title",
  testimonials: "home.section.testimonial.title",
  profile: "appBar.userProfile.title",
  addCase: "case.creator.label",
};

/**
 * The sections of a case are grouped in the path. A group has no page of its
 * own, so it is no step of the way to a section.
 */
const CASE_SECTION_GROUPS = ["configuration", "analytics"];

function labelOf(
  segments: string[],
  index: number,
  t: TFunction,
  openCase: NamedEntity | undefined,
): string {
  const segment = segments[index];
  const [root, , group] = segments;

  // the path has the id of the case, the user knows it by its name
  if (root === "cases" && index === 1) {
    return openCase?.id === segment ? openCase.name : segment;
  }

  const key =
    index === 0
      ? ROOT_LABEL_KEYS[segment]
      : root === "portfolio" && index === 1
        ? `portfolio.section.${segment}`
        : root === "cases" && index === 3
          ? `case.details.section.${group}.${segment}`
          : undefined;

  // a step the app has no name for shows as it is in the path
  return key ? t(key, { defaultValue: segment }) : segment;
}

/**
 * The way from the home page to the page at a path, one breadcrumb per page
 * on it.
 */
export function breadcrumbsOf(
  pathname: string,
  t: TFunction,
  openCase?: NamedEntity,
): Breadcrumb[] {
  const segments = pathname
    .split("/")
    .filter((segment) => segment.trim().length > 0);

  return segments.flatMap((segment, index) =>
    segments[0] === "cases" &&
    index === 2 &&
    CASE_SECTION_GROUPS.includes(segment)
      ? []
      : [
          {
            label: labelOf(segments, index, t, openCase),
            to: "/" + segments.slice(0, index + 1).join("/"),
          },
        ],
  );
}
